import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UserOutlined } from '@ant-design/icons'
import { Avatar, Table } from 'antd'
import Skeleton from 'react-loading-skeleton'

import B2becTable from '@/components/B2becTable'
import FormatPrice from '@/components/FormatPrice'
import FormatStatus from '@/components/FormatStatus'
import CartItemName from '@/pages/Cart/CartItemName'
import { useAppSelector } from '@/store/hook'
import { selectHideVoucher } from '@/store/selectors/configSelector'
import { useDummyItems } from '@/hooks'
import { formatQuantity } from '@/libs/utils/handleNumber'

import OrderDetailsCartSummary from './components/CartTable/OrderDetailsCartSummary'
import styles from './OrderDetailsTable.module.scss'

const Shipments = ({ shipments }) => {
  const [shipmentURL] = shipments.match(/(https?:\/\/\S+)/g)
  const shipmentNr = shipments.replace(shipmentURL, '').replace(':', '')

  return (
    <a
      key={shipmentNr}
      className="shipmentLink"
      href={shipmentURL}
      rel="noopener noreferrer"
      target="_blank"
      title={shipmentNr}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      {shipmentNr}
    </a>
  )
}

const OrderDetailsTable = ({ orderItems, isLoading, onRow }) => {
  const { t } = useTranslation()
  const fakeItems = useDummyItems()
  const hideVoucher = useAppSelector(selectHideVoucher)

  const currencyUnit = orderItems?.currencyUnit
  const items = useMemo(() => {
    const shipmentsTrackingLinks = orderItems?.shipments

    const mappedItemsWithShipments = orderItems?.items.map((item, index) => {
      return {
        ...item,
        shipments: shipmentsTrackingLinks[index],
      }
    })

    return mappedItemsWithShipments
  }, [orderItems])

  const dataSource = useMemo(
    () => (isLoading ? fakeItems : items),
    [fakeItems, isLoading, items],
  )

  const shouldShowRecyclingFee = !!orderItems?.checkoutSettings?.positionLvlConditions

  return (
    <div className={styles.orderDetailsTable}>
      <B2becTable
        dataSource={dataSource}
        rowKey="materialNumber"
        summary={() =>
          orderItems && <OrderDetailsCartSummary {...orderItems} hideVoucher={hideVoucher} />}
        onRow={onRow}
      >
        <Table.Column
          dataIndex="imgUrl"
          width="10%"
          render={(imgUrl) =>
            isLoading
              ? <Skeleton />
              : imgUrl
              ? <Avatar shape="square" size={64} src={imgUrl} />
              : <Avatar icon={<UserOutlined />} shape="square" size={64} />}
        />
        <Table.Column
          dataIndex="materialNumber"
          render={(text) => (isLoading ? <Skeleton /> : <span>{text}</span>)}
          title={t('cart.table.itemNo')}
          width="15%"
        />
        <Table.Column
          dataIndex="name"
          title={t('cart.table.itemName')}
          render={(_, record) => {
            return isLoading ? <Skeleton /> : (
              <>
                <CartItemName
                  data={{ ...record, currencyUnit }}
                  shouldShowRecyclingFee={shouldShowRecyclingFee}
                />
                <FormatStatus status={record?.status} />
                {!record?.isAvailable
                  ? (
                    <p className="unavailable-product__text">
                      {t('errors.unavailableProductDetails')}
                    </p>
                  )
                  : null}
                {record?.shipments ? <Shipments shipments={record?.shipments} /> : null}
              </>
            )
          }}
        />
        <Table.Column
          align="center"
          dataIndex="quantity"
          title={t('cart.table.quantity')}
          width="13%"
          render={(text) => isLoading ? <Skeleton /> : <span>{formatQuantity(text)}</span>}
        />
        <Table.Column
          align="right"
          dataIndex="price"
          title={t('cart.table.individualPrice')}
          width="15%"
          render={(text) =>
            isLoading ? <Skeleton /> : <FormatPrice currencyUnit={currencyUnit} price={text} />}
        />
        <Table.Column
          align="right"
          dataIndex="total"
          title={t('total')}
          width="15%"
          render={(text) =>
            isLoading ? <Skeleton /> : (
              <FormatPrice
                className="text--bold"
                currencyUnit={currencyUnit}
                price={text}
              />
            )}
        />
      </B2becTable>
    </div>
  )
}

export default OrderDetailsTable
